<template>
  <ServicePage
    service-id="managed-services"
    :monthly-cost="monthlyCost"
    :item-count="lines.length"
    :can-add="canAdd"
    @add="addLines"
  >
    <div class="space-y-6">
      <!-- General -->
      <ConfigSection
        section-key="general"
        title="VM maintenance & security advisory"
        subtitle="Patch management, health maintenance and security hardening"
        v-model:enabled="general.enabled"
        :monthly-cost="cost(generalLine)"
      >
        <div class="space-y-5">
          <PriceTable :entries="generalEntries" />
          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <NumberField v-model="general.vms" label="Managed VMs" />
            <NumberField
              v-model="general.vmPrice"
              label="Average monthly price per managed VM"
              unit="€"
              :step="0.01"
              hint="The maintenance fee is charged together with 10% of the price of the VM being managed."
            />
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <NumberField v-model="general.hosts" label="Hosts under security advisory" />
            <NumberField v-model="general.pods" label="Pods under security advisory" />
          </div>
          <p v-if="securityAdvisory" class="text-xs text-slate-500">
            {{ securityAdvisory.description }}
          </p>
        </div>
      </ConfigSection>

      <!-- Rancher -->
      <ConfigSection
        section-key="rancher"
        title="Rancher / Kubernetes"
        subtitle="Managed Rancher clusters and downstream cluster management"
        v-model:enabled="rancher.enabled"
        :monthly-cost="cost(rancherLine)"
      >
        <div class="space-y-5">
          <PriceTable :entries="rancherEntries" />
          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <NumberField v-model="rancher.management" label="Managed Rancher installations" />
            <NumberField v-model="rancher.clusters" label="Ready-to-go Rancher clusters" />
            <NumberField v-model="rancher.downstream" label="Downstream clusters" />
            <NumberField v-model="rancher.shared" label="Shared Rancher instances" />
          </div>
        </div>
      </ConfigSection>

      <!-- IAM -->
      <ConfigSection
        section-key="iam"
        title="Identity & access management"
        subtitle="Managed IDP"
        v-model:enabled="iam.enabled"
        :monthly-cost="cost(iamLine)"
      >
        <div class="space-y-5">
          <PriceTable :entries="iamEntries" selectable :selected="iam.plan" @select="iam.plan = $event" />
          <NumberField
            v-if="iam.plan && iam.plan.type === 'pro'"
            v-model="iam.users"
            label="Users"
            hint="Charged per user per month on the pro tier."
          />
          <p v-else class="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-3">
            The base tier is free of charge for the first 10,000 users.
          </p>
        </div>
      </ConfigSection>

      <!-- DNS -->
      <ConfigSection
        section-key="dns"
        title="Anycast DNS"
        subtitle="Hosted zones, records and queries"
        v-model:enabled="dns.enabled"
        :monthly-cost="cost(dnsLine)"
      >
        <div class="space-y-5">
          <PriceTable :entries="dnsEntries" />
          <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
            <NumberField
              v-model="dns.zones"
              label="Hosted zones"
              :hint="`The first ${FREE_ZONE_TIER} zones are charged at the standard rate.`"
            />
            <NumberField
              v-model="dns.records"
              label="Records per zone"
              :hint="`Up to ${RECORDS_INCLUDED.toLocaleString()} records per zone are included.`"
            />
            <NumberField
              v-model="dns.queries"
              label="Queries"
              unit="million/month"
              :hint="`The rate drops above ${QUERY_TIER_M.toLocaleString()} million queries.`"
            />
          </div>
        </div>
      </ConfigSection>

      <!-- Email -->
      <ConfigSection
        section-key="email"
        title="Transactional email"
        subtitle="Commitment tiers for outgoing email"
        v-model:enabled="email.enabled"
        :monthly-cost="cost(emailLine)"
      >
        <PriceTable :entries="emailEntries" selectable :selected="email.plan" @select="email.plan = $event" />
      </ConfigSection>

      <!-- Support -->
      <ConfigSection
        section-key="support"
        title="Support plan"
        subtitle="Priced as a flat fee or a share of your infrastructure, whichever is higher"
        v-model:enabled="support.enabled"
        :monthly-cost="cost(supportLine)"
      >
        <div class="space-y-4">
          <PriceTable
            :entries="supportEntries"
            selectable
            :selected="support.plan"
            @select="support.plan = $event"
          />
          <p class="text-sm text-indigo-800 bg-indigo-50 border border-indigo-200 rounded p-3">
            The support cost follows your estimate: it is recalculated automatically whenever you add
            or remove services, so it always reflects the higher of the flat fee and the percentage of
            your total infrastructure cost.
          </p>
        </div>
      </ConfigSection>

      <CostPreview v-if="lines.length" :lines-prop="lines" />
    </div>
  </ServicePage>
</template>

<script>
import { mapGetters } from "vuex";
import ServicePage from "@/components/config/ServicePage.vue";
import ConfigSection from "@/components/config/ConfigSection.vue";
import NumberField from "@/components/config/NumberField.vue";
import PriceTable from "@/components/config/PriceTable.vue";
import CostPreview from "@/components/config/CostPreview.vue";
import { configurable } from "@/mixins/configurable";
import { byType, estimateLine, lineItem, priceOf, round } from "@/utils/pricing";

// "The fee is always charged together with a 10% fee of the price of the vm"
const VM_PRICE_SHARE = 0.1;
// "per hosted zone per month (first 25 hosted zones)"
const FREE_ZONE_TIER = 25;
// "A hosted zone includes up to 10,000 records"
const RECORDS_INCLUDED = 10000;
// "first 1 billion queries per month", expressed in millions
const QUERY_TIER_M = 1000;

/** Support tiers state their share in the description ("... or 10% of ..."). */
const shareFromDescription = (description) => {
  const match = /(\d+(?:\.\d+)?)\s*%/.exec(description || "");
  return match ? Number(match[1]) / 100 : 0;
};

export default {
  name: "ManagedServicesService",
  components: { ServicePage, ConfigSection, NumberField, PriceTable, CostPreview },
  mixins: [configurable],
  data() {
    return {
      FREE_ZONE_TIER,
      RECORDS_INCLUDED,
      QUERY_TIER_M,
      general: { enabled: true, vms: 0, vmPrice: 0, hosts: 0, pods: 0 },
      rancher: { enabled: false, management: 0, clusters: 0, downstream: 0, shared: 0 },
      iam: { enabled: false, plan: null, users: 0 },
      dns: { enabled: false, zones: 0, records: 0, queries: 0 },
      email: { enabled: false, plan: null },
      support: { enabled: false, plan: null },
    };
  },
  computed: {
    ...mapGetters({ section: "pricing/section" }),

    generalEntries() {
      return this.section("services.general") || [];
    },
    rancherEntries() {
      return this.section("services.rancher") || [];
    },
    iamEntries() {
      return this.section("services.iam") || [];
    },
    dnsEntries() {
      return this.section("services.dns") || [];
    },
    emailEntries() {
      return this.section("services.email") || [];
    },
    supportEntries() {
      return this.section("services.support") || [];
    },
    securityAdvisory() {
      return byType(this.generalEntries, "securitadvisory");
    },

    generalLine() {
      const { enabled, vms, vmPrice, hosts, pods } = this.general;
      if (!enabled) return null;

      const maintenance = byType(this.generalEntries, "maintenance");
      const advisory = this.securityAdvisory;
      const items = [];

      const vmCount = Math.max(0, Number(vms) || 0);
      if (vmCount > 0) {
        const perVm = priceOf(maintenance);
        const share = VM_PRICE_SHARE * Math.max(0, Number(vmPrice) || 0);
        items.push(
          lineItem({
            label: "VM maintenance",
            detail: `${vmCount} VM(s) × (${round(perVm)} € + 10% of ${round(
              Number(vmPrice) || 0
            )} €)`,
            quantity: vmCount,
            unit: "month",
            unitPrice: perVm + share,
          })
        );
      }

      // "The minimum price per host is 68.75 EUR" -- a floor on the per-pod charge.
      const hostCount = Math.max(0, Number(hosts) || 0);
      const podCount = Math.max(0, Number(pods) || 0);
      if (hostCount > 0 || podCount > 0) {
        const podCost = podCount * priceOf(advisory);
        const minimum = hostCount * this.hostMinimum;
        const cost = Math.max(podCost, minimum);
        items.push(
          lineItem({
            label: "Security advisory",
            detail:
              minimum > podCost
                ? `${hostCount} host(s) × ${this.hostMinimum} € minimum (above ${podCount} pod(s))`
                : `${podCount} pod(s) × ${priceOf(advisory)} €`,
            quantity: 1,
            unit: "month",
            unitPrice: cost,
            cost,
          })
        );
      }

      if (!items.length) return null;

      return estimateLine({
        key: "services.general",
        serviceId: "managed-services",
        name: "Managed Services General",
        description: "VM maintenance and security advisory",
        config: { vms: vmCount, vmPrice: Number(vmPrice) || 0, hosts: hostCount, pods: podCount },
        items,
      });
    },

    rancherLine() {
      const { enabled, management, clusters, downstream, shared } = this.rancher;
      if (!enabled) return null;

      const items = [];
      const add = (type, count, label) => {
        const quantity = Math.max(0, Number(count) || 0);
        if (quantity <= 0) return;
        const entry = byType(this.rancherEntries, type);
        items.push(
          lineItem({
            label,
            detail: `${quantity} × ${entry?.description ? label : type}`,
            quantity,
            unit: "month",
            unitPrice: priceOf(entry),
          })
        );
      };

      add("management", management, "Rancher management");
      add("cluster", clusters, "Rancher cluster");
      add("downstream", downstream, "Downstream cluster management");
      add("shared", shared, "Shared Rancher instance");

      if (!items.length) return null;

      return estimateLine({
        key: "services.rancher",
        serviceId: "managed-services",
        name: "Managed Services Rancher",
        description: "Rancher and Kubernetes management",
        config: {
          management: Number(management) || 0,
          clusters: Number(clusters) || 0,
          downstream: Number(downstream) || 0,
          shared: Number(shared) || 0,
        },
        items,
      });
    },

    iamLine() {
      const { enabled, plan, users } = this.iam;
      if (!enabled || !plan) return null;

      const userCount = Math.max(0, Number(users) || 0);
      const isPro = plan.type === "pro";
      if (isPro && userCount <= 0) return null;

      return estimateLine({
        key: "services.iam",
        serviceId: "managed-services",
        name: `Managed IAM (${plan.type})`,
        description: plan.description,
        config: { type: plan.type, users: userCount },
        items: [
          lineItem({
            label: `Managed IDP — ${plan.type}`,
            detail: isPro ? `${userCount.toLocaleString()} user(s)` : plan.description,
            quantity: isPro ? userCount : 1,
            unit: isPro ? "user/month" : "month",
            unitPrice: priceOf(plan),
          }),
        ],
      });
    },

    dnsLine() {
      const { enabled, zones, records, queries } = this.dns;
      if (!enabled) return null;

      const items = [];
      const zoneCount = Math.max(0, Number(zones) || 0);

      if (zoneCount > 0) {
        const standardZones = Math.min(zoneCount, FREE_ZONE_TIER);
        const additionalZones = Math.max(0, zoneCount - FREE_ZONE_TIER);

        items.push(
          lineItem({
            label: "Hosted zones",
            detail: `${standardZones} zone(s)`,
            quantity: standardZones,
            unit: "zone/month",
            unitPrice: priceOf(byType(this.dnsEntries, "hostedZone")),
          })
        );

        if (additionalZones > 0) {
          items.push(
            lineItem({
              label: "Additional hosted zones",
              detail: `${additionalZones} zone(s) beyond the first ${FREE_ZONE_TIER}`,
              quantity: additionalZones,
              unit: "zone/month",
              unitPrice: priceOf(byType(this.dnsEntries, "hostedZoneAdditional")),
            })
          );
        }

        const extraRecords = Math.max(0, (Number(records) || 0) - RECORDS_INCLUDED);
        if (extraRecords > 0) {
          items.push(
            lineItem({
              label: "Additional records",
              detail: `${extraRecords.toLocaleString()} record(s) per zone × ${zoneCount} zone(s)`,
              quantity: extraRecords * zoneCount,
              unit: "record/month",
              unitPrice: priceOf(byType(this.dnsEntries, "extraRecords")),
            })
          );
        }
      }

      const queryM = Math.max(0, Number(queries) || 0);
      if (queryM > 0) {
        const tier1 = Math.min(queryM, QUERY_TIER_M);
        const tier2 = Math.max(0, queryM - QUERY_TIER_M);

        items.push(
          lineItem({
            label: "DNS queries",
            detail: `${round(tier1)} million queries`,
            quantity: tier1,
            unit: "M queries",
            unitPrice: priceOf(byType(this.dnsEntries, "queries")),
          })
        );

        if (tier2 > 0) {
          items.push(
            lineItem({
              label: "DNS queries above 1 billion",
              detail: `${round(tier2)} million queries`,
              quantity: tier2,
              unit: "M queries",
              unitPrice: priceOf(byType(this.dnsEntries, "queriesOver1B")),
            })
          );
        }
      }

      if (!items.length) return null;

      return estimateLine({
        key: "services.dns",
        serviceId: "managed-services",
        name: "Anycast DNS",
        description: "Hosted zones, records and queries",
        config: {
          zones: zoneCount,
          records: Number(records) || 0,
          queries: queryM,
        },
        items,
      });
    },

    emailLine() {
      const { enabled, plan } = this.email;
      if (!enabled || !plan) return null;

      return estimateLine({
        key: "services.email",
        serviceId: "managed-services",
        name: `Transactional Email (${plan.type})`,
        description: plan.description,
        config: { type: plan.type },
        items: [
          lineItem({
            label: "Email commitment",
            detail: plan.description,
            quantity: 1,
            unit: "month",
            unitPrice: priceOf(plan),
          }),
        ],
      });
    },

    supportLine() {
      const { enabled, plan } = this.support;
      if (!enabled || !plan) return null;

      const share = shareFromDescription(plan.description);

      return estimateLine({
        key: "services.support",
        serviceId: "managed-services",
        name: `Support plan (${plan.type})`,
        description: plan.description,
        config: { type: plan.type },
        items: [
          lineItem({
            label: "Support plan",
            detail: plan.description,
            quantity: 1,
            unit: "month",
            unitPrice: priceOf(plan),
          }),
        ],
        dynamic: "support",
        meta: { flatPrice: priceOf(plan), share },
      });
    },

    hostMinimum() {
      // Stated in the security advisory description: "minimum price per host is 68.75 EUR"
      const match = /minimum price per host is\s*([\d.,]+)/i.exec(
        this.securityAdvisory?.description || ""
      );
      return match ? Number(match[1].replace(",", ".")) : 68.75;
    },

    lines() {
      return [
        this.generalLine,
        this.rancherLine,
        this.iamLine,
        this.dnsLine,
        this.emailLine,
        this.supportLine,
      ].filter((line) => line && (line.monthlyCost > 0 || line.dynamic));
    },
  },
  watch: {
    iamEntries: {
      immediate: true,
      handler(list) {
        if (!this.iam.plan && list.length) this.iam.plan = list[0];
      },
    },
    emailEntries: {
      immediate: true,
      handler(list) {
        if (!this.email.plan && list.length) this.email.plan = list[0];
      },
    },
    supportEntries: {
      immediate: true,
      handler(list) {
        if (!this.support.plan && list.length) this.support.plan = list[0];
      },
    },
  },
  methods: {
    cost(line) {
      return line ? line.monthlyCost : 0;
    },
  },
};
</script>
